import React, { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import config from '../../../config/api';
import styles from './SignalRequestModal.module.css';

interface SignalRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SignalRequestModal: React.FC<SignalRequestModalProps> = ({ isOpen, onClose }) => {
  const { user, userUuid, getToken } = useAuth();
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!description.trim()) return;

    setSending(true);
    setError('');

    try {
      const token = await getToken();
      const response = await fetch(`${config.apiUrl}/signal-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userUuid,
          user_name: user?.name || '',
          user_email: user?.email || '',
          description: description.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      setSent(true);
      setTimeout(() => {
        setSent(false);
        setDescription('');
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to send request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3 className={styles.title}>Request a Signal</h3>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <p className={styles.subtitle}>
          Describe the signal or indicator you'd like us to add to the backtest engine.
        </p>

        <textarea
          className={styles.textarea}
          placeholder="e.g., I'd like a MACD crossover signal that triggers when the MACD line crosses above/below the signal line..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={5}
          disabled={sending || sent}
        />

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.footer}>
          <span className={styles.userInfo}>{user?.email}</span>
          <button
            className={styles.submitButton}
            onClick={handleSubmit}
            disabled={!description.trim() || sending || sent}
          >
            {sending ? (
              <><Loader2 size={16} className={styles.spinner} /> Sending...</>
            ) : sent ? (
              'Sent ✓'
            ) : (
              <><Send size={16} /> Submit Request</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
